// The three Flipkart-style blocks on the mobile home feed.
//
//   <GreatFindsSection />  a 3-up product grid, built from the categories
//                          this visitor actually browses (see
//                          categoryAffinity.js), or everyday essentials for
//                          someone arriving for the first time.
//   <TileSection />        a 2x2 grid of category tiles with a round arrow
//                          in the header. Used for Trending and for Hair &
//                          Skincare.
//
// Mobile only -- App.js renders these behind an isMobileUI() check, so the
// desktop home page is untouched.

import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { mediaUrl } from '../config';
import { IconArrowRight, IconFlame, IconSparkle } from '../Icons';
import { RatingPill } from '../ProductRating';
import { getFeedCategories } from './categoryAffinity';

// Rank within a category: better-rated and more-reviewed first, so a section
// with six slots shows the six worth showing rather than the six that happen
// to sort first by id.
function byAppeal(a, b) {
  const rating = (Number(b.avgRating) || 0) - (Number(a.avgRating) || 0);
  if (Math.abs(rating) > 0.05) return rating;
  return (Number(b.reviewCount) || 0) - (Number(a.reviewCount) || 0);
}

const inStock = (p) => p && p.stock !== 0 && p.status !== 'hidden';

/**
 * "Great finds for you" — six products drawn from the visitor's own most-
 * browsed categories.
 *
 * Fills across categories rather than taking six from the top one: someone
 * who browses Electronics and Beauty should see both, not six phone cases.
 * If the preferred categories cannot fill six slots (a thin catalogue, or
 * everything in them out of stock) the remainder comes from the rest of the
 * catalogue by the same ranking, so the grid is never short.
 */
export const GreatFindsSection = memo(function GreatFindsSection({ products, formatCurrency }) {
  const { items, personalised } = useMemo(() => {
    const all = (products || []).filter(inStock);
    if (all.length === 0) return { items: [], personalised: false };

    const { categories, personalised: isPersonal } = getFeedCategories(3);

    const picked = [];
    const seen = new Set();
    // Round-robin across the preferred categories so the strongest one does
    // not crowd out the others.
    const buckets = categories.map((c) =>
      all.filter((p) => p.category === c).sort(byAppeal)
    );
    // Deepest bucket decides how many rounds are possible; iterating by index
    // rather than closing over a mutable loop variable keeps this free of the
    // no-loop-func pitfall (and CRA fails the CI build on that warning).
    const deepest = buckets.reduce((m, b) => Math.max(m, b.length), 0);
    for (let round = 0; round < deepest && picked.length < 6; round++) {
      for (let i = 0; i < buckets.length && picked.length < 6; i++) {
        const p = buckets[i][round];
        if (p && !seen.has(p.id)) { picked.push(p); seen.add(p.id); }
      }
    }

    if (picked.length < 6) {
      for (const p of [...all].sort(byAppeal)) {
        if (picked.length >= 6) break;
        if (!seen.has(p.id)) { picked.push(p); seen.add(p.id); }
      }
    }

    return { items: picked.slice(0, 6), personalised: isPersonal };
  }, [products]);

  if (items.length < 3) return null;

  return (
    <section className="mf-section mf-finds">
      <header className="mf-head">
        <h2 className="mf-title">Great finds for you</h2>
        {/* Says why these six and not six others. "Sponsored" in the
            reference is a paid placement; this is not one, so it says what
            is actually true instead. */}
        <p className="mf-sub">
          {personalised ? 'Based on what you browse' : 'Everyday essentials'}
        </p>
      </header>

      <div className="mf-grid">
        {items.map((p) => {
          const sale = p.salePrice && p.saleEndsAt && new Date(p.saleEndsAt) > new Date();
          const price = sale ? p.salePrice : p.price;
          const was = sale ? p.price : p.originalPrice;
          return (
            <Link key={p.id} to={`/product/${p.id}`} className="mf-card">
              <span className="mf-card-img-wrap">
                <img
                  className="mf-card-img"
                  src={mediaUrl(p.image)}
                  alt={p.name}
                  loading="lazy"
                  width="112"
                  height="112"
                />
                {Number(p.avgRating) > 0 && (
                  <span className="mf-card-rating">
                    <RatingPill rating={p.avgRating} />
                  </span>
                )}
              </span>
              <span className="mf-card-name">{p.name}</span>
              <span className="mf-card-price-row">
                <span className="mf-card-price">{formatCurrency(price)}</span>
                {/* Strikethrough only. The discount RATE stays in the
                    checkout order summary, as everywhere else. */}
                {was && was > price && (
                  <span className="mf-card-was">{formatCurrency(was)}</span>
                )}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
});

/**
 * A 2x2 block of category tiles with a round arrow in the header — the
 * "Trending Gadgets & Appliances" / "Hair & Skincare Essentials" shape.
 *
 * Each tile is a product standing in for a group, with a label and a claim
 * ("Min. 50% Off", "Special offer") underneath.
 */
export const TileSection = memo(function TileSection({ title, tone = 'a', items, onSeeAll, seeAllLabel }) {
  if (!items || items.length < 4) return null;
  return (
    <section className={`mf-section mf-tiles mf-tiles-${tone}`}>
      {/* Same header shape as CategoryRow ("Top Sales"): title on the left,
          a "View all →" text link on the right. The round arrow button this
          replaced came from the reference screenshot, but the app already had
          an established way of saying "there is more of this" and two
          different ones on the same feed read as two different products. */}
      <header className="mf-head mf-head-row">
        <h2 className="mf-title">{title}</h2>
        {onSeeAll && (
          <button
            type="button"
            className="cat-row-viewall mf-viewall"
            onClick={onSeeAll}
            aria-label={seeAllLabel || `View all ${title}`}
          >
            View all <IconArrowRight size={12} />
          </button>
        )}
      </header>

      <div className="mf-tile-grid">
        {items.slice(0, 4).map((t) => (
          <Link key={t.id} to={t.href} className="mf-tile">
            <span className="mf-tile-img-wrap">
              <img className="mf-tile-img" src={mediaUrl(t.image)} alt="" loading="lazy" width="150" height="150" />
            </span>
            <span className="mf-tile-label">{t.label}</span>
            <span className="mf-tile-claim">{t.claim}</span>
          </Link>
        ))}
      </div>
    </section>
  );
});

/**
 * Builds the four tiles for a section from a pool of products.
 *
 * `claimFor` decides the line under each tile. It is passed the product so a
 * caller can say something true about it -- the biggest genuine saving in the
 * group, say -- rather than a decorative number.
 */
/**
 * The exact saving on a product, as a whole percentage — or null when there
 * is none.
 *
 * Deliberately mirrors what the product card renders, so a tile and a card
 * for the same product can never disagree: the "now" price is the sale price
 * while a sale is LIVE (saleEndsAt still in the future) and the list price
 * otherwise, and the "was" price is whichever figure the card strikes
 * through. An earlier version skipped the saleEndsAt check, so an expired
 * sale still produced a discount.
 */
export function exactDiscountPct(p) {
  if (!p) return null;
  const saleLive = p.salePrice && p.saleEndsAt && new Date(p.saleEndsAt) > new Date();
  const now = saleLive ? p.salePrice : p.price;
  const was = saleLive ? p.price : p.originalPrice;
  if (!was || !now || was <= now) return null;
  return Math.round(((was - now) / was) * 100);
}

export function buildTiles(products, { categories, limit = 4, claimFor }) {
  const pool = (products || [])
    .filter((p) => inStock(p) && (!categories || categories.includes(p.category)))
    .sort(byAppeal);

  const out = [];
  const seenLabels = new Set();
  for (const p of pool) {
    if (out.length >= limit) break;
    // One tile per product name-stem, so a section is not four colourways of
    // the same shirt.
    const label = p.name.split(/[-–—|,(]/)[0].trim().slice(0, 28);
    const key = label.toLowerCase();
    if (seenLabels.has(key)) continue;
    seenLabels.add(key);
    out.push({
      id: p.id,
      href: `/product/${p.id}`,
      image: p.image,
      label,
      claim: claimFor ? claimFor(p) : '',
    });
  }
  return out;
}

export { IconFlame, IconSparkle };
